import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { useState } from "react";

// custom component imports
import style from "./discrepantSpecimen.module.css";
import { colors } from "../../theme/colors";
import Table from "../../components/DataTable/Table";
import { CustomTab } from "../../components";
import { CustomMenu } from "../../components/Menu";

//Icons imports
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { ReactComponent as Qr } from "../../assets/svg/Qr.svg";
import { ReactComponent as Lab } from "../../assets/svg/Lab.svg";
import { ReactComponent as Signature } from "../../assets/svg/Signature.svg";
import { ReactComponent as Disease } from "../../assets/svg/Disease.svg";
import { ReactComponent as Specimens } from "../../assets/svg/specimen.svg";

const DiscrepantSpecimenForm = () => {
  const [query] = useState("");
  const [selectedTab, setSelectedTab] = useState("Request Form");

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const tabs = [
    { label: "Request Form", value: "Request Form" },
    { label: "Event Log", value: "Event Log" },
    { label: "Discrepancy Details", value: "Discrepancy Details" },
  ];

  // NOTE: this is a placeholder screen. Do NOT hardcode patient PHI (names,
  // MRNs, DOBs) or clinician names here — populate from the API when wired up.
  const doctorInfo = [
    { label: "Doctor Full Name", value: "" },
    { label: "Room", value: "" },
    { label: "Hospital ID", value: "" },
  ];

  const patientInfo = [
    { label: "Full Name", value: "" },
    { label: "Date Of Birth", value: "" },
    { label: "Surgeon", value: "" },
    { label: "Gender", value: "" },
    { label: "MRN", value: "" },
  ];

  const laboratoryInfo = [
    { label: "Laboratory Name", value: "" },
    { label: "Work Address", value: "" },
    { label: "Email", value: "" },
    { label: "Phone", value: "" },
  ];

  // for event log table
  const fields = [
    { id: "name", value: "Specimen", flex: 1 },
    { id: "mrn", value: "Timestamp", type: "address", flex: 1 },
    {
      id: "drop_off_time",
      value: "User",
      flex: 1.2,
    },
    { id: "action", value: "Action", flex: 1 },
  ];

  const logData = {
    items: [] as Array<{
      name: string;
      mrn: string;
      drop_off_time: string;
      action: string;
    }>,
    count: 0,
  };

  // Discrepancy details — labels only; values come from the API, never hardcoded PHI.
  const hospitalDetail = [
    {
      label: "Hospital",
      value: "",
    },
    {
      label: "Patient Name",
      value: "",
    },
    {
      label: "Patient MRN",
      value: "",
    },
    {
      label: "Patient DOB",
      value: "",
    },
    {
      label: "Doctor",
      value: "",
    },
  ];

  const discrepancyField = [
    { id: "name", value: "Specimen", flex: 2 },
    { id: "storage", value: "Storage" },
    {
      id: "collection_time",
      value: "Collection Date & Time",
    },
    { id: "overall_status", value: "Status", type: "statusBadge" },
  ];

  // Placeholder table — populate from the API when this screen is wired up.
  const discrepancyDetail = {
    items: [] as Array<{
      name: string;
      storage: JSX.Element;
      collection_time: string;
      overall_status: string;
    }>,
    count: 0,
  };

  // for custom menu
  const handleEdit = () => {
    // TODO: wire up edit action when this screen is connected to the API
  };

  const handleArchive = () => {
    // TODO: wire up archive action when this screen is connected to the API
  };

  const menuItems = [
    {
      icon: <EditIcon />,
      text: "Edit",
      onClick: handleEdit,
    },
    {
      icon: <ArchiveIcon />,
      text: "Archive",
      onClick: handleArchive,
    },
  ];

  return (
    <Box mt="20px">
      <CustomTab
        options={tabs}
        selectedTab={selectedTab}
        handleChange={handleChange}
      />

      {/* Request form  */}
      {selectedTab === "Request Form" && (
        <Box className={style.boxContainer}>
          <Typography p={2} variant="h4" fontWeight="bold">
            Request Form
          </Typography>
          <Grid container spacing={4}>
            <Grid item textAlign="center" mt={1} xs={2.2}>
              <Typography className={style.lightHeading}>
                Request Form QR Code
              </Typography>
              <Qr className={style.qrContainer} />
              <Typography variant="h5" fontWeight="bold" />
            </Grid>

            <Grid item xs={9.8}>
              <Typography variant="h5" fontWeight="bold">
                Laboratory
              </Typography>
              <Box
                border="none"
                display="flex"
                minHeight="184px"
                className={style.container}
              >
                {/* first grid colums  */}
                <Box mt={2}>
                  <Lab />
                </Box>
                <Grid container mx={3}>
                  {laboratoryInfo.map((e) => {
                    return (
                      <Grid item key={e.label} xs={6}>
                        <Typography mt={2} className={style.lightHeading}>
                          {e.label}
                        </Typography>
                        <Typography className={style.heading}>
                          {e.value}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Grid>
          </Grid>
          {/* Patient detail and operating details  */}
          <Grid container spacing={3} mt={0}>
            <Grid xs={6} item>
              <Typography variant="h5" fontWeight="bold">
                Patient Details
              </Typography>
              <Box minHeight="230px" className={style.container}>
                <Grid container>
                  {patientInfo.map((e) => {
                    return (
                      <Grid item key={e.label} xs={6}>
                        <Typography mt={2} className={style.lightHeading}>
                          {e.label}
                        </Typography>
                        <Typography className={style.heading}>
                          {e.value}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Grid>
            <Grid xs={6} item>
              <Typography variant="h5" fontWeight="bold">
                Operating Details
              </Typography>
              <Box minHeight="230px" className={style.container}>
                <Grid container>
                  {doctorInfo.map((e) => {
                    return (
                      <Grid item key={e.label} xs={6}>
                        <Typography mt={2} className={style.lightHeading}>
                          {e.label}
                        </Typography>
                        <Typography className={style.heading}>
                          {e.value}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Grid>
          </Grid>
          {/* Clinic notes and copy to section  */}
          <Grid container spacing={3} mt={0}>
            <Grid xs={6} item>
              <Typography variant="h5" fontWeight="bold">
                Client Notes:
              </Typography>
              <textarea className={clsx(style.textArea, style.container)} />
            </Grid>
            <Grid xs={6} item>
              <Typography variant="h5" fontWeight="bold">
                Copy to:
              </Typography>
              <Box className={style.container}>
                <Typography mt={2} className={style.lightHeading} />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h5" fontWeight="bold">
                Tests
              </Typography>
              <textarea className={clsx(style.textArea, style.container)} />
            </Grid>
          </Grid>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox defaultChecked />}
            label={
              <Typography className={style.heading}>Urgent report </Typography>
            }
          />
          <Typography my={1} className={style.heading}>
            Report needed by
          </Typography>
          <FormControlLabel
            control={<Checkbox defaultChecked />}
            label={
              <>
                <Typography className={style.heading}>
                  Send reports to{" "}
                  <Link sx={{ color: colors.lightBlack }}>
                    My Health Record
                  </Link>
                </Typography>
              </>
            }
          />
          <Typography my={1} variant="h5" fontWeight="bold">
            Doctor’s Signature
          </Typography>
          <Signature />
          <Typography my={1} variant="body1" fontWeight="light">
            Date Signed:
          </Typography>
          <Typography my={3} variant="h5" fontWeight="bold">
            This Document has an attachments
          </Typography>
          <Disease />
          <Disease />
          <Disease />
        </Box>
      )}

      {/* Event log */}
      {selectedTab === "Event Log" && (
        <Box className={style.tableContainer}>
          <Table<any>
            fields={fields}
            data={logData}
            filters={{ title: query }}
            onChange={() => ""}
            defaultOrderBy="name"
            paginationRequired={false}
          />
        </Box>
      )}
      {/* Discrepancy Details  */}
      {selectedTab === "Discrepancy Details" && (
        <Box bgcolor={colors.white} className={style.boxContainer}>
          <Box className={style.flexTop}>
            {hospitalDetail.map((e: any) => (
              <Box key={e.label} className={style.row}>
                <Typography width="15%">{e.label}</Typography>
                <Typography fontWeight="bold">{e.value}</Typography>
              </Box>
            ))}
            <Box className={style.row}>
              <Typography width="15%">Overall Status</Typography>
              <CustomMenu label="Action Required" menuItems={menuItems} />
            </Box>
          </Box>
          <Box py={2}>
            <Table<any>
              fields={discrepancyField}
              data={discrepancyDetail}
              paginationRequired={false}
              filters={{ title: query }}
              onChange={() => ""}
              defaultOrderBy="name"
            />
          </Box>
          <Box display="flex" alignItems="center">
            <AttachFileIcon fontSize="medium" sx={{ rotate: "45deg" }} />
            <Typography px={2} className={style.mediumHeading}>
              Notes
            </Typography>
          </Box>
          <Box
            justifyContent="space-between"
            className={clsx(style.boxContainer, style.row)}
          >
            <Typography width="40%">
              <Link fontWeight="bold" sx={{ color: colors.dark }}>
                5. Biopsy, Small intestine, Disaccardiase <br />
              </Link>
              Label is Printed but no specimen taken 2 in bag not 3
            </Typography>
            <Typography fontWeight="bold" />
            <Specimens className={style.imgBorder} />
          </Box>
          <Box
            justifyContent="space-between"
            className={clsx(style.boxContainer, style.row)}
          >
            <Typography width="40%">
              <Link fontWeight="bold" sx={{ color: colors.dark }}>
                5. Biopsy, Small intestine, Disaccardiase <br />
              </Link>
              Label is Printed but no specimen taken 2 in bag not 3
            </Typography>
            <Typography fontWeight="bold" />
            <Box>No image found !</Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DiscrepantSpecimenForm;
